import { useCallback, useEffect, useMemo, useState } from "react";
import ReviewerSidebar from "../components/ReviewerSidebar";
import api from "../services/api";
import toast from "react-hot-toast";

export default function ReviewerReviewedSubmissions() {
  const [search, setSearch] = useState("");
  const [reviewedEsmps, setReviewedEsmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchReviewedEsmps = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await api.get("/esmp/my-esmps");
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      const reviewed = data.filter((item) =>
        ["Approved", "Returned", "Rejected"].includes(item.status)
      );

      setReviewedEsmps(reviewed);
    } catch (err) {
      const message = err.response?.data?.message || "Failed to load reviewed ESMPs";
      console.error("Failed to fetch reviewed ESMPs:", err);
      toast.error(message);
      setError(message);
      setReviewedEsmps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviewedEsmps();
  }, [fetchReviewedEsmps]);

  const filtered = useMemo(
    () =>
      reviewedEsmps.filter((e) =>
        (e?.esmp_id || "").toLowerCase().includes(search.toLowerCase()) ||
        (e?.district || "").toLowerCase().includes(search.toLowerCase()) ||
        (e?.project_name || "").toLowerCase().includes(search.toLowerCase())
      ),
    [reviewedEsmps, search]
  );

  return (
    <div className="flex min-h-screen bg-gray-100">
      <ReviewerSidebar />
      <main className="flex-1 p-6 space-y-6">
        <div className="flex justify-between items-center bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md">
          <h2 className="text-2xl font-bold">Reviewer Reviewed Submissions</h2>
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-2 rounded text-black border focus:ring-2 focus:ring-green-600 outline-none"
            />
            <button
              onClick={fetchReviewedEsmps}
              className="px-4 py-2 bg-white text-green-700 rounded-lg font-semibold hover:bg-gray-100"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow">
          {loading ? (
            <p className="text-gray-500">Loading reviewed ESMPs...</p>
          ) : error ? (
            <p className="text-red-600">{error}</p>
          ) : filtered.length === 0 ? (
            <p className="text-gray-500">No reviewed ESMPs found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-3 py-2 text-left">ESMP ID</th>
                    <th className="border px-3 py-2 text-left">Project Name</th>
                    <th className="border px-3 py-2 text-left">District</th>
                    <th className="border px-3 py-2 text-left">Status</th>
                    <th className="border px-3 py-2 text-left">Reviewed Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => (
                    <tr key={item.id} className="hover:bg-green-50">
                      <td className="border px-3 py-2">{item.esmp_id}</td>
                      <td className="border px-3 py-2">{item.project_name}</td>
                      <td className="border px-3 py-2">{item.district}</td>
                      <td className="border px-3 py-2">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            item.status === "Approved"
                              ? "bg-green-100 text-green-800"
                              : item.status === "Returned"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="border px-3 py-2 text-xs text-gray-600">
                        {new Date(item.updatedAt || item.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
